import FlexBox from '@/atomic-components/FlexBox';
import { getColor } from '@/utils/theme/colors';
import { getStyle } from '@/utils/theme/fonts';
import { Box, Button, Drawer, Grid, Radio, Typography } from '@mui/material';
import RightArrowIcon from '../../../../assets/pdp/rightArrowIcon.svg';
import BubbleIcon from '../../../../assets/pdp/bubble.svg';
import ConditionsAndInfo from './conditionAndInfo';
import { useEffect, useMemo, useRef, useState } from 'react';
import ProductGroups from './ProductGroups';
import { IOfferData, IOffers, IStaticData } from '../../model';
import VariantConfiguration from './VariantConfiguration';
import { Conditions } from '../../staticDataModal';
import PDPOffers from './Offers';
import ConditionIcon from '../../../../assets/pdp/conditionIcon.svg';
import CrossIcon from '../../../../assets/pdp/crossIcon.svg';
import TenureSelectorDrawer from '@/components/TenureSelectorDrawer';
import { useRouter } from 'next/router';
import { useLocation } from '@/hooks/useLocation';
import NCEMIPlanDetail from './EMIPlanDrawer';
import WishlistIcon from './wishlistIcon';
import { Collection } from '@/services/catalogue/models/pdp';
import { LencoTypography } from '@/atomic-components/LencoTypography';
import { getInterSectionObserver } from '@/atomic-components/Image/utils/intersectionObserverHelper';
import { useNotification } from '@/hooks/useNotification';
import { NotificationSeverity } from '@/components/NotifyToast';
import { ConfigurationsByLop } from '../../LopBasedConfugration';
import ArrowWithColorIcon from '@/assets/pdp/svgwithProps/arrowWithCircle1';
import Link from 'next/link';
import { emitMoeEvent } from '@/utils/dataLayer';
import { layoutService } from '@/services/layout-service';
import PaymentPlanIcon from '@/assets/pdp/paymentPlanIcon.svg';
import PPArrow from '@/assets/pdp/ppArrow.svg';
import PPCheck from '@/assets/pdp/ppCheck.svg';
import { PaymentPlan } from '../TenureSelectorV2/model';
import KycRequirementInfoCard from '../KycRequirementInfoCard';

const CustomPdpBannersByLop = ({
	lineOfProduct,
}: {
	lineOfProduct: string;
}) => {
	const customBanners = ConfigurationsByLop.get(lineOfProduct)?.customBanners;

	return (
		<Box mb='40px'>
			{customBanners?.map((banner) => {
				return (
					<Link
						href={banner.cta.data.actionData.deeplinkPath}
						key={banner.title}
					>
						<FlexBox
							className='cursor-pointer'
							alignItems='center'
							sx={{
								p: '16px 24px 16px 24px',
								borderRadius: '8px',
								backgroundColor: getColor(banner.unhoveredStyles.cardBgColor),
								border: `solid 1px ${getColor(
									banner.unhoveredStyles.borderColor
								)}`,
								':hover': {
									backgroundColor: getColor(banner.hoveredStyles.cardBgColor),
									border: `solid 1px ${getColor(
										banner.hoveredStyles.borderColor
									)}`,
									'> .customBannerRightArrowBorder': {
										border: `solid 1px ${getColor(
											banner.hoveredStyles.borderColor
										)}`,
									},
									'> .titleImageUnhovered': {
										display: 'none',
									},
									'> .titleImageHovered': {
										display: 'block',
									},
								},
							}}
						>
							<Box
								sx={{
									width: '40px',
									height: '40px',
								}}
							>
								<img
									className='titleImageUnhovered'
									height='40px'
									width='40px'
									src={banner.titleImageUrl.hovered}
								/>
								<img
									className='titleImageHovered'
									height='40px'
									width='40px'
									src={banner.titleImageUrl.unhovered}
									style={{ display: 'none' }}
								/>
							</Box>
							<Box m='0px 12px'>
								<LencoTypography variant='P_Medium' color={getColor('Gray 0')}>
									{banner.title}
								</LencoTypography>
								<LencoTypography
									variant='Small_Medium'
									color={getColor(banner.subTitle.color)}
									mt='8px'
								>
									{banner.subTitle.content}
								</LencoTypography>
							</Box>
							<Box
								className='customBannerRightArrowBorder'
								sx={{
									width: '27px',
									height: '26px',
									borderRadius: '50%',
									border: `solid 1px ${getColor(
										banner.unhoveredStyles.borderColor
									)}`,
								}}
							>
								<ArrowWithColorIcon
									fill='white'
									strokeColor={getColor('Gray 0')}
								/>
							</Box>
						</FlexBox>
					</Link>
				);
			})}
		</Box>
	);
};

type PDPSideBarComponentInterface = {
	productTitle: any;
	productId: number;
	condition: string;
	promiseDate: any;
	paymentValue: any;
	upfrontOffers?: any;
	offers?: IOfferData;
	productGroup: any;
	priceBreakUp: any;
	lineOfProduct: string;
	variantConfiguration: any;
	variantGroupId: number;
	label: string;
	setConfiguration: Function;
	isAvailable: boolean;
	handleWishlist: Function;
	isProductWishlisted: boolean;
	addingItemToCart: boolean;
	setTenureSelect: Function;
	tenureSelect: number;
	setBottomAddToCart: Function;
	collection: Collection[];
	availableUnits: number;
	CartActionButton: React.ReactElement;
};

const static_data: IStaticData = {
	trust_markers: [
		{
			text: 'Free Delivery and Installation',
			image: {
				url: 'https://assets.furlenco.com/s3-furlenco-images/evolve_2_0/delivery-truck.png',
				aspectRatio: 1,
			},
		},
		{
			text: 'Sanitized for your Safety',
			image: {
				url: 'https://assets.furlenco.com/s3-furlenco-images/evolve_2_0/door-delivery.png',
				aspectRatio: 1,
			},
		},
	],
	conditions: {
		BUY_NEW:
			"All our products are expertly restored to their original condition at our state of art refurbishment centre. However, for few products like appliances and pre-lam furniture, please expect minor scratches or dents which won't affect the aesthetics or functionality of the product.",
		BUY_REFURBISHED:
			"All our products are expertly restored to their original condition at our state of art refurbishment centre. However, for few products like appliances and pre-lam furniture, please expect minor scratches or dents which won't affect the aesthetics or functionality of the product.",
		RENT: "All our products are expertly restored to their original condition at our state of art refurbishment centre. However, for few products like appliances and pre-lam furniture, please expect minor scratches or dents which won't affect the aesthetics or functionality of the product.",
	},
	important_terms: {
		BUY_NEW:
			'<ul><li>Furlenco shall provide shipping and delivery in your order to the registered address if it falls in the areas serviceable by the company. Unless otherwise notified, shipping and delivery for all products is free of charge.&nbsp;</li><li>Your shipping address and pin code will be verified for serviceability for each product prior to you making payment for such a Product. If your order is not serviceable by Furlenco, then you may provide an alternate shipping address within our serviceable area.&nbsp;</li><li>We endeavour to deliver all products within the promised timeline (at the time of purchase). We will keep you informed via call, SMS, and e-Mail about the progress of your order. You can also enquire about the status of the order by writing to customersupport@furlenco.com. However, we acknowledge that the delivery may get delayed due to circumstances not within the control of the Company. We will keep you informed in case of a delay in delivery.</li><li>&nbsp;Once the Products ordered by you reach a nearby delivery center, our delivery representatives will make 2 (two) delivery attempts, beyond which if you do not take the delivery of the Product, your order is subject to cancellation.</li></ul>',
		BUY_REFURBISHED:
			'<ul><li>Furlenco shall provide shipping and delivery in your order to the registered address if it falls in the areas serviceable by the company. Unless otherwise notified, shipping and delivery for all products is free of charge.&nbsp;</li><li>Your shipping address and pin code will be verified for serviceability for each product prior to you making payment for such a Product. If your order is not serviceable by Furlenco, then you may provide an alternate shipping address within our serviceable area.&nbsp;</li><li>We endeavour to deliver all products within the promised timeline (at the time of purchase). We will keep you informed via call, SMS, and e-Mail about the progress of your order. You can also enquire about the status of the order by writing to customersupport@furlenco.com. However, we acknowledge that the delivery may get delayed due to circumstances not within the control of the Company. We will keep you informed in case of a delay in delivery.</li><li>&nbsp;Once the Products ordered by you reach a nearby delivery center, our delivery representatives will make 2 (two) delivery attempts, beyond which if you do not take the delivery of the Product, your order is subject to cancellation.</li></ul>',
		RENT: '<ul><li>Furlenco shall provide shipping and delivery in your order to the registered address if it falls in the areas serviceable by the company. Unless otherwise notified, shipping and delivery for all products is free of charge.&nbsp;</li><li>Your shipping address and pin code will be verified for serviceability for each product prior to you making payment for such a Product. If your order is not serviceable by Furlenco, then you may provide an alternate shipping address within our serviceable area.&nbsp;</li><li>We endeavour to deliver all products within the promised timeline (at the time of purchase). We will keep you informed via call, SMS, and e-Mail about the progress of your order. You can also enquire about the status of the order by writing to customersupport@furlenco.com. However, we acknowledge that the delivery may get delayed due to circumstances not within the control of the Company. We will keep you informed in case of a delay in delivery.</li><li>&nbsp;Once the Products ordered by you reach a nearby delivery center, our delivery representatives will make 2 (two) delivery attempts, beyond which if you do not take the delivery of the Product, your order is subject to cancellation.</li></ul>',
	},
};

const PDPSideBarComponent = (props: PDPSideBarComponentInterface) => {
	const ATCRef = useRef();
	const [openTenureSelector, setOpenTenureSelector] = useState(false);
	const [conditionDrawer, setConditionDrawer] = useState(false);
	const [WishlistIconColor, setWishlistIconColor] = useState('');
	const { selectedLocation, locationActions } = useLocation();
	const [openEMIPlan, setOpenEMIPlan] = useState(false);
	const { actions } = useNotification();
	const [tenureMessages, setTenureMessages] = useState<any>();
	const [tenureLabels, setTenureLabels] = useState<any>();

	const currentlySelectedPincode = selectedLocation.pincode;

	const priceValue = props?.upfrontOffers?.paymentPlans.filter((data: any) => {
		return data.tenure === props?.tenureSelect;
	});

	const productPrice = props?.upfrontOffers?.paymentPlans?.filter(
		(pay: any) => {
			return pay.tenure == props?.tenureSelect;
		}
	)[0];

	const EMIDataHeader = props?.offers?.data?.ncEmiOffer?.header;
	const EMIDataDes = props?.offers?.data?.ncEmiOffer?.description;
	const EMIMetaData = props?.offers?.data?.ncEmiOffer?.meta;
	const bgColor = props.condition == 'RENT' ? 'Orange -5' : 'Navy -5';
	const rentTenure =
		props?.upfrontOffers?.tenureSelector?.upfrontPaymentPlans?.paymentPlans;

	const handleTanureSelection = (context: string) => {
		setOpenTenureSelector(true);
		emitMoeEvent('PDP Tenure Selector Opened', {
			product_id: props.productId,
			widget: context,
		});
	};
	const ATCObserver = getInterSectionObserver();

	const observer = useMemo(
		() =>
			new IntersectionObserver(([entry]) => {
				if (!entry.isIntersecting) {
					props?.setBottomAddToCart(true);
				} else {
					props?.setBottomAddToCart(false);
				}
			}),
		[]
	);

	const updateScrollValue = () => {
		if (ATCObserver && ATCRef) {
			ATCRef?.current && observer.observe(ATCRef?.current);
		}
	};

	useEffect(() => {
		updateScrollValue();
		layoutService.getKeys('TENURE_MESSAGES').then((res: any) => {
			setTenureMessages(res?.data?.data?.tenures);
		});
		let tenureData: any = {
			monthly: [],
			upfront: [],
		};

		let tenureMap: any = {};
		if (props?.upfrontOffers && props?.upfrontOffers?.tenureSelector) {
			Object.values(props?.upfrontOffers?.tenureSelector)?.map((d: any) => {
				d?.paymentPlans?.map((plans: any) => {
					if (plans?.tenure == 1) {
						tenureData.monthly.push({
							tenure: plans.tenure,
							displayValue: plans.label,
						});
					} else {
						tenureData.upfront.push({
							tenure: plans.tenure,
							displayValue: plans.label,
						});
					}
					tenureMap = { ...tenureMap, [plans?.tenure]: plans?.selectorLabel };
				});
			});
			setTenureLabels(tenureMap);
			// setTenures(tenureData)
		}
		return () => {
			if (ATCObserver) {
				observer.disconnect();
			}
		};
	}, []);

	const router = useRouter();
	return (
		<FlexBox
			direction='column'
			sx={{
				padding: '32px',
				backgroundColor: getColor(`${bgColor}`),
			}}
		>
			<Grid
				display='flex'
				direction='row'
				justifyContent='space-between'
				alignItems='center'
			>
				<LencoTypography
					sx={{ ...getStyle('Small_Medium'), color: getColor('Gray -3') }}
				>
					{props.condition == 'RENT'
						? 'RENT'
						: props.condition == 'BUY_NEW'
						? 'BRAND NEW'
						: 'REFURBISHED'}
				</LencoTypography>
				<FlexBox>
					<Box
						onClick={() => props.handleWishlist()}
						sx={{
							cursor: 'pointer',
							backgroundColor: `${WishlistIconColor}`,
							alignSelf: 'start',
						}}
					>
						<WishlistIcon
							color={props?.isProductWishlisted ? '#047992' : '#FFFFFF'}
						/>
					</Box>
				</FlexBox>
			</Grid>
			<FlexBox sx={{ justifyContent: 'space-between', width: '100%' }}>
				<FlexBox direction='column' sx={{ marginBottom: '40px' }}>
					<LencoTypography
						component={'h1'}
						sx={{
							...getStyle('H9_Medium'),
							color: getColor('Gray 0'),
						}}
					>
						{props?.productTitle}
					</LencoTypography>
					{!props?.isAvailable && (
						<LencoTypography
							sx={{
								...getStyle('Small_SemiBold'),
								color: getColor('Danger -2'),
							}}
						>
							OUT OF STOCK
						</LencoTypography>
					)}
				</FlexBox>
			</FlexBox>
			<FlexBox
				sx={{
					justifyContent: 'space-between',
					flexWrap: 'wrap',
				}}
			>
				<FlexBox
					sx={{
						justifyContent: 'center',
						alignItems: 'center',
						flexWrap: 'wrap',
					}}
				>
					<Box sx={{ mt: '2px' }}>
						<BubbleIcon />
					</Box>
					<LencoTypography
						sx={{
							...getStyle('P_SemiBold'),
							color: getColor('Gray 0'),
							paddingLeft: '8px',
							paddingRight: '8px',
						}}
					>
						{props.condition == 'RENT' ? 'Rent' : 'Buy'}{' '}
						<LencoTypography
							component='span'
							sx={{ ...getStyle('P_Regular'), color: getColor('Gray -2') }}
						>
							{' '}
							{props.condition == 'RENT'
								? props?.upfrontOffers?.tenureSelector?.upfrontPaymentPlans
										?.paymentPlans === null ||
								  props?.upfrontOffers?.tenureSelector?.upfrontPaymentPlans
										?.paymentPlans.length === 0
									? 'at'
									: 'starting at'
								: props.condition == 'BUY_NEW'
								? 'Brand New'
								: 'Refurbished'}
						</LencoTypography>
					</LencoTypography>
					<LencoTypography
						component='span'
						sx={{ ...getStyle('P_Regular'), color: getColor('Gray -2') }}
					>
						{' '}
						{props.paymentValue?.displayValue}
					</LencoTypography>
					{/* {props.condition == 'RENT' && (
						<FlexBox
							sx={{
								alignItems: 'center',
								justifyContent: 'center',
								// width: '120px',
								height: '36px',
								padding: '0px 20px 0px 20px',
								borderRadius: '24px',
								border: `1px solid ${getColor('Gray -2')}`,
								backgroundColor: '#FFFFFF',
								cursor: 'pointer',
								'&:hover': {
									'#tenure-button': {
										transform: 'rotate(-45deg)',
										transition: '0.3s all',
									},
								},
							}}
							onClick={() => {
								handleTanureSelection('PDP Tenure');
							}}
						>
							<LencoTypography
								sx={{
									...getStyle('Tiny_Regular'),
									cursor: 'pointer',
									color: getColor('Gray 0'),
								}}
							>
							{props?.tenureSelect} {props?.tenureSelect > 1 ?'months':'month'} 
								{tenureLabels && tenureLabels[props.tenureSelect]}
								<Box
									id='tenure-button'
									sx={{
										border: 'solid #222222',
										borderWidth: ' 0 1px 1px 0',
										display: 'inline-block',
										padding: '3px',
										transform: 'rotate(45deg)',
										margin: '0px 4px 0px 4px',
									}}
								/>
							</LencoTypography>
						</FlexBox>
					)} */}
				</FlexBox>

				<FlexBox sx={{ justifyContent: 'center', alignItems: 'center' }}>
					<LencoTypography
						sx={{
							...getStyle('Small_Regular'),
							textDecoration: 'line-through',
							color: getColor('Gray -2'),
						}}
					>
						{props.condition == 'RENT'
							? priceValue[0]?.strikePrice?.displayValue
							: props.priceBreakUp.strikePrice.displayValue}
					</LencoTypography>
					<LencoTypography
						sx={{
							...getStyle('P_SemiBold'),
							color: '#222222',
							paddingLeft: '8px',
						}}
					>
						{props.condition == 'RENT'
							? `${priceValue[0]?.totalDiscountPercentage.displayValue} OFF`
							: `${props.priceBreakUp.baseDiscountPercentage.displayValue} OFF`}
					</LencoTypography>
				</FlexBox>
			</FlexBox>
			{/* {props?.condition == 'RENT' && <LencoTypography style={{color:getColor('Gray 0') ,...getStyle('Tiny-Medium'),marginTop:'12px'}}>*{tenureMessages && tenureMessages[props?.tenureSelect]?.message}</LencoTypography>} */}

			<Box ref={ATCRef}>
				<Box>{props.CartActionButton}</Box>
			</Box>
			<ConditionsAndInfo
				icon={'deliveryTruck'}
				text={`${!props?.isAvailable ? 'OUT OF STOCK' : 'Get it by'}`}
				description={`${
					!props?.isAvailable
						? ''
						: `${props.promiseDate.displayValue} to ${currentlySelectedPincode}`
				}`}
				linkText='change pincode'
				handleClick={() => {
					locationActions.triggerCitySelector(true);
					emitMoeEvent('PDP Promise Date Pincode Clicked', {
						product_id: props.productId,
						lineofproduct: props.lineOfProduct,
					});
				}}
			/>
			<ConditionsAndInfo
				icon={'condition'}
				text={`Condition :`}
				description={
					props.lineOfProduct == 'RENT'
						? 'As good as new'
						: props.lineOfProduct == 'BUY_NEW'
						? 'Brand new products'
						: 'Fully Remanufactured'
				}
				linkText='know more'
				handleClick={() => setConditionDrawer(true)}
			/>
			{/* {EMIDataHeader && (
				<ConditionsAndInfo
					icon={'savings'}
					text={`${EMIDataHeader}`}
					description={`${EMIDataDes}`}
					linkText='View Plans'
					handleClick={() => setOpenEMIPlan(true)}
				/>
			)} */}
			{props.productGroup && Object.keys(props.productGroup).length > 0 && (
				<ProductGroups
					productGroups={props.productGroup}
					rentStartingPrice={
						props.upfrontOffers?.paymentPlans[0]?.monthlyRental?.displayValue
					}
				/>
			)}

			{props.variantConfiguration && (
				<VariantConfiguration
					variantGroupId={props.variantGroupId}
					variantConfiguration={props.variantConfiguration}
					label={props.productTitle}
					condition={props.condition}
					priceData={priceValue}
					promiseDate={props.promiseDate}
					setConfiguration={props.setConfiguration}
					selectedTenure={props?.tenureSelect}
					isAvailable={props?.isAvailable}
				/>
			)}

			<CustomPdpBannersByLop lineOfProduct={props.lineOfProduct} />

			{props?.offers && (
				<Box sx={{ width: '100%' }}>
					<PDPOffers
						offers={props.offers}
						productId={props?.productId}
						collections={props?.collection}
						lineOfProduct={props?.lineOfProduct}
						productPrice={
							productPrice?.monthlyRental?.value
								? productPrice?.monthlyRental?.value
								: props.priceBreakUp.postTax.value
						}
						disountRatio={
							props.condition == 'RENT'
								? `${priceValue[0]?.totalDiscountPercentage.value}`
								: `${props.priceBreakUp.baseDiscountPercentage.value}`
						}
						strikePrice={
							props.condition == 'RENT'
								? priceValue[0]?.strikePrice?.value
								: props.priceBreakUp.strikePrice.value
						}
						currentTenure={props?.tenureSelect}
						stockFlag={props?.isAvailable ? 'true' : 'false'}
					/>
				</Box>
			)}
			{/* {props?.condition == 'RENT' && <Box p='20px 24px' bgcolor={'white'} borderRadius={'8px'} border={`1px solid ${getColor('Gray -6')}`} sx={{cursor:'pointer',mt:'28px'}}
			onClick={() => handleTanureSelection('Payment Plan Tiles')}
			>
				<FlexBox sx={{justifyContent:'space-between',alignItems:'center'}}>
					<FlexBox sx={{gap:1,alignItems:'center'}}>
						<Box>
							<PaymentPlanIcon/>
						</Box>
						<Typography sx={{...getStyle('H14_Medium'),color:getColor('Gray -1')}}>Payment Plans</Typography>
					</FlexBox>
					<Typography sx={{...getStyle('Small_Medium'),color:getColor('Minty +1'),textDecoration:'underline'}}>View Plans <span style={{paddingLeft:'4px'}}><PPArrow/></span></Typography>
				</FlexBox>
				<Box sx={{padding:'8px 0'}}>
					{Object.keys(props?.upfrontOffers?.tenureSelector)?.map((re:any,index)=>{
						const res=props?.upfrontOffers?.tenureSelector[re]
						return(
							<>
							{re == 'upfrontPaymentPlans' && <Typography sx={{...getStyle('Small_medium'),color:getColor('Gray 0')}}>Pay upfront for</Typography>}
							<FlexBox key={index} sx={{padding:'16px 0',gap:1}}>
								{res?.paymentPlans?.map((plan:PaymentPlan)=>{
									return (
										<Box key={plan.tenure} sx={{padding:'12px 16px', color:props?.tenureSelect == plan.tenure ? getColor('Minty +1'):getColor('Gray 0'),borderRadius:'4px',border:`1px solid ${getColor('Gray -4')}`,boxShadow:'0px 8px 16px 0px #22222214',backgroundColor:props?.tenureSelect == plan.tenure ? getColor('Navy -6'):'white'}}>
											<Typography sx={{...getStyle('Small_SemiBold'),display:'flex',alignItems:'center',gap:'2px'}}>{props?.tenureSelect == plan.tenure && <PPCheck/>}{plan.label}</Typography>
										</Box>
									)
								})}
							</FlexBox>
							</>
							
						)
					})} 
				</Box>
			</Box>} */}
			<TenureSelectorDrawer
				productId={props.productId}
				open={openTenureSelector}
				onClose={() => setOpenTenureSelector(false)}
				isUpdatingTenure={false}
				currentTenure={props?.tenureSelect}
				quantity={1}
				upfrontOffers={props?.upfrontOffers}
				onTenureChange={(selectedTenure: number) => {
					props.setTenureSelect(selectedTenure);
					setOpenTenureSelector(false);
					actions.notifyUser({
						message: 'Tenure updated successfully!',
						severity: NotificationSeverity.SUCCESS,
					});
				}}
				screen='PDP'
			/>

			<Drawer
				anchor='right'
				open={conditionDrawer}
				onClose={() => setConditionDrawer(false)}
			>
				<FlexBox direction='column' sx={{ maxWidth: '480px', padding: '80px' }}>
					<FlexBox
						sx={{ justifyContent: 'space-between', paddingBottom: '36px' }}
					>
						<ConditionIcon />
						<Box
							onClick={() => setConditionDrawer(false)}
							sx={{ cursor: 'pointer' }}
						>
							<CrossIcon />
						</Box>
					</FlexBox>
					<LencoTypography
						sx={{
							...getStyle('H9_Regular'),
							color: getColor('Gray 0'),
							paddingBottom: '30px',
						}}
					>
						Condition
					</LencoTypography>
					<LencoTypography>
						{
							static_data.conditions[
								`${props.lineOfProduct as keyof Conditions}`
							]
						}
					</LencoTypography>
				</FlexBox>
			</Drawer>

			<NCEMIPlanDetail
				NCEMIOffer={props?.offers?.data?.ncEmiOffer}
				openEMIPlan={openEMIPlan}
				setOpenEMIPlan={() => setOpenEMIPlan(false)}
			/>
			<KycRequirementInfoCard
				lineOfProduct={props?.lineOfProduct}
				promisedDate={props?.promiseDate?.displayValue}
			/>
		</FlexBox>
	);
};

export default PDPSideBarComponent;
